import { useState, useMemo } from "react";
import { useCustom, useCustomMutation, type HttpError } from "@refinedev/core";
import {
  Bot,
  Settings as SettingsIcon,
  ArrowUpDown,
  Check,
  ChevronsUpDown,
  Wrench,
  Route,
  Info,
  Zap,
  Brain,
  Search,
  MessageSquare,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { notify } from '@/lib/notify';
import { translateErrorMessage } from "@/providers/notification-provider";
import { Separator } from "@/components/ui/separator";
import { FormHeader, FormStickyActions, FormSpacer } from "@/components/forms";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { usePersistedState } from "@/hooks/use-persisted-state";
import { useScrollRestoration } from "@/hooks/use-scroll-restoration";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";

interface AIConfig {
  model?: string;
  systemPrompt?: string;
  isChatEnabled?: boolean;
  enableTools?: boolean;
  enableRouter?: boolean;
  routerModel?: string;
}

type ModelPricing = {
  prompt?: string | null;
  completion?: string | null;
} & Record<string, string | null | undefined>;

interface OpenRouterModelSummary {
  id: string;
  name: string;
  description: string | null;
  pricing: ModelPricing;
  isFree: boolean;
  inputModalities: string[];
  outputModalities: string[];
  contextLength: number | null;
  maxOutputTokens: number | null;
  maxInputTokens: number | null;
}

const formatTokens = (value: number | null | undefined) =>
  typeof value === "number" ? value.toLocaleString("ru-RU") : "н/д";

const modalityLabels: Record<string, string> = {
  text: "текст",
  audio: "аудио",
  image: "изображения",
  video: "видео",
  embeddings: "эмбеддинги",
};

const formatModalities = (values: string[]) => {
  if (values.length === 0) {
    return "н/д";
  }

  return values.map((value) => modalityLabels[value] ?? value).join(", ");
};

const formatPricing = (model: OpenRouterModelSummary) => {
  if (model.isFree) {
    return "бесплатно";
  }

  const prompt = model.pricing?.prompt;
  const completion = model.pricing?.completion;

  if (!prompt && !completion) {
    return "платно";
  }

  const parts = [];
  if (prompt) {
    parts.push(`ввод ${prompt}`);
  }
  if (completion) {
    parts.push(`вывод ${completion}`);
  }

  return parts.join(", ");
};

type SortField = "name" | "contextLength" | "maxOutputTokens" | "inputCost" | "outputCost";
type SortDirection = "asc" | "desc";

interface SortOption {
  field: SortField;
  direction: SortDirection;
  label: string;
}

const SORT_OPTIONS: SortOption[] = [
  { field: "name", direction: "asc", label: "Название (А-Я)" },
  { field: "name", direction: "desc", label: "Название (Я-А)" },
  { field: "contextLength", direction: "desc", label: "Контекст (больше)" },
  { field: "contextLength", direction: "asc", label: "Контекст (меньше)" },
  { field: "maxOutputTokens", direction: "desc", label: "Макс. выход (больше)" },
  { field: "maxOutputTokens", direction: "asc", label: "Макс. выход (меньше)" },
  { field: "inputCost", direction: "asc", label: "Цена ввода (дешевле)" },
  { field: "inputCost", direction: "desc", label: "Цена ввода (дороже)" },
  { field: "outputCost", direction: "asc", label: "Цена вывода (дешевле)" },
  { field: "outputCost", direction: "desc", label: "Цена вывода (дороже)" },
];

const defaultConfig: AIConfig = {
  model: "",
  systemPrompt: "",
  isChatEnabled: false,
  enableTools: true,
  enableRouter: false,
  routerModel: "openai/gpt-4o-mini",
};

/**
 * AIList
 *
 * Список ai с фильтрацией и управлением
 *
 * @returns {React.ReactElement} React компонент
 */
export const AIList = () => {
  useScrollRestoration();
  const [activeTab, setActiveTab] = usePersistedState("ai:tab", "settings");
  const [showFreeOnly, setShowFreeOnly] = usePersistedState("ai:freeOnly", false);
  const [sortIndex, setSortIndex] = usePersistedState("ai:sortIndex", 0);
  const [modelSelectOpen, setModelSelectOpen] = useState(false);
  const [routerModelSelectOpen, setRouterModelSelectOpen] = useState(false);
  const [routerInfoOpen, setRouterInfoOpen] = useState(false);

  const currentSort = SORT_OPTIONS[sortIndex];

  const modelsUrl = useMemo(() => {
    const params = new URLSearchParams();
    if (showFreeOnly) {
      params.set("free", "true");
    }
    if (currentSort) {
      params.set("sort", `${currentSort.field}:${currentSort.direction}`);
    }
    const queryString = params.toString();
    return queryString ? `/ai/models?${queryString}` : "/ai/models";
  }, [showFreeOnly, currentSort]);

  const settingsResult = useCustom<AIConfig>({
    url: "/ai/config/global_settings",
    method: "get",
  });
  const settingsData = settingsResult.result;
  const isLoadingSettings = settingsResult.result === undefined;
  const refetchSettings = settingsResult.query?.refetch ?? (() => {});

  const modelsResult = useCustom<OpenRouterModelSummary[]>({
    url: modelsUrl,
    method: "get",
  });
  const modelsData = modelsResult.result;
  const isLoadingModels = modelsResult.result === undefined;

  const [configOverrides, setConfigOverrides] = useState<Partial<AIConfig>>({});
  const config: AIConfig = {
    ...defaultConfig,
    ...settingsData?.data,
    ...configOverrides,
  };

  const setConfig = (updates: Partial<AIConfig>) => {
    setConfigOverrides((prev) => ({ ...prev, ...updates }));
  };

  const { mutate: updateSettings, mutation: updateSettingsMutation } =
    useCustomMutation<AIConfig>();
  const isUpdating = updateSettingsMutation.isPending;

  const handleSaveSettings = () => {
    updateSettings(
      {
        url: "/ai/config/global_settings",
        method: "put",
        values: {
          value: config,
          isActive: true,
        },
      },
      {
        onSuccess: () => {
          notify.success("Настройки успешно сохранены");
          setConfigOverrides({});
          refetchSettings();
        },
        onError: (error: HttpError) => {
          notify.error("Ошибка сохранения настроек: " + translateErrorMessage(error.message));
        },
      }
    );
  };

  // --- Playground Tab State ---
  const [prompt, setPrompt] = useState("");
  const [chatResponse, setChatResponse] = useState("");

  const { mutate: sendChat, mutation: sendChatMutation } = useCustomMutation<{
    answer: string;
  }>();
  const isChatting = sendChatMutation.isPending;

  const handleSendChat = () => {
    if (!prompt.trim()) {return;}

    setChatResponse("");
    sendChat(
      {
        url: "/ai/chat",
        method: "post",
        values: {
          message: prompt,
          userId: 1,
        },
      },
      {
        onSuccess: (res) => {
          setChatResponse(res.data.answer);
        },
        onError: (err: HttpError) => {
          setChatResponse("Ошибка: " + err.message);
        },
      }
    );
  };

  const modelValue = config.model?.trim() ?? "";
  const modelsRaw = modelsData?.data;
  const models = Array.isArray(modelsRaw) ? modelsRaw : [];

  const selectedModel =
    models.find((model: OpenRouterModelSummary) => model.id === modelValue) ??
    null;

  const routerModelValue = config.routerModel?.trim() ?? "openai/gpt-4o-mini";
  const selectedRouterModel =
    models.find((model: OpenRouterModelSummary) => model.id === routerModelValue) ??
    null;

  return (
    <div className="space-y-6 pb-20">
      <FormHeader
        title="Управление ИИ"
        description="Настройка интеграции ИИ и тестирование ответов"
        sticky
      />

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList>
          <TabsTrigger value="settings">
            <SettingsIcon className="h-4 w-4 mr-2" />
            Настройки
          </TabsTrigger>
          <TabsTrigger value="playground">
            <Bot className="h-4 w-4 mr-2" />
            Песочница
          </TabsTrigger>
        </TabsList>

        <TabsContent value="settings" forceMount className="mt-6 data-[state=inactive]:hidden">
          <div className="max-w-3xl space-y-6">
          {isLoadingSettings ? (
            <Card>
              <CardContent className="p-8 text-center text-muted-foreground">
                Загрузка настроек...
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Карточка 1: Основные настройки */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <SettingsIcon className="h-5 w-5" />
                    Основные настройки
                  </CardTitle>
                  <CardDescription>
                    Включение чата и выбор основной модели
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Переключатель чата */}
                  <div className="flex items-center justify-between rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <Label htmlFor="chat-enabled" className="font-medium">
                        Включить ИИ-чат глобально
                      </Label>
                      <p className="text-sm text-muted-foreground">
                        Позволяет пользователям общаться с AI-ассистентом
                      </p>
                    </div>
                    <Switch
                      id="chat-enabled"
                      checked={config.isChatEnabled}
                      onCheckedChange={(checked) =>
                        setConfig({ isChatEnabled: checked })
                      }
                    />
                  </div>

                  {/* Выбор модели */}
                  <div className="space-y-3">
                    <Label>Основная модель</Label>

                    {/* Фильтры и сортировка */}
                    <div className="flex flex-wrap items-center gap-3">
                      <div className="flex items-center space-x-2">
                        <Switch
                          id="free-only"
                          checked={showFreeOnly}
                          onCheckedChange={setShowFreeOnly}
                        />
                        <Label htmlFor="free-only" className="text-sm">Только бесплатные</Label>
                      </div>

                      <div className="flex items-center gap-2">
                        <ArrowUpDown className="h-4 w-4 text-muted-foreground" />
                        <Select
                          value={sortIndex.toString()}
                          onValueChange={(value) => setSortIndex(Number(value))}
                        >
                          <SelectTrigger className="w-[200px]">
                            <SelectValue placeholder="Сортировка" />
                          </SelectTrigger>
                          <SelectContent>
                            {SORT_OPTIONS.map((option, index) => (
                              <SelectItem key={index} value={index.toString()}>
                                {option.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {/* Combobox для выбора модели */}
                    <Popover open={modelSelectOpen} onOpenChange={setModelSelectOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          role="combobox"
                          aria-expanded={modelSelectOpen}
                          className="w-full justify-between font-normal"
                          disabled={isLoadingModels}
                        >
                          {isLoadingModels ? (
                            "Загрузка моделей..."
                          ) : (modelValue ? (
                            selectedModel?.name || modelValue
                          ) : (
                            "Выберите модель..."
                          ))}
                          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
                        <Command>
                          <CommandInput placeholder="Поиск модели..." />
                          <CommandList>
                            <CommandEmpty>Модели не найдены</CommandEmpty>
                            <CommandGroup>
                              {models.map((model) => (
                                <CommandItem
                                  key={model.id}
                                  value={`${model.name} ${model.id}`}
                                  onSelect={() => {
                                    setConfig({ model: model.id });
                                    setModelSelectOpen(false);
                                  }}
                                >
                                  <Check
                                    className={cn(
                                      "mr-2 h-4 w-4",
                                      modelValue === model.id ? "opacity-100" : "opacity-0"
                                    )}
                                  />
                                  <div className="flex flex-col">
                                    <span>{model.name || model.id}</span>
                                    {model.isFree && (
                                      <span className="text-xs text-muted-foreground">бесплатно</span>
                                    )}
                                  </div>
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>

                    {/* Статистика моделей */}
                    <div className="text-sm text-muted-foreground">
                      {isLoadingModels ? "Загрузка..." : `Доступно: ${models.length} моделей`}
                    </div>

                    {/* Информация о выбранной модели */}
                    {selectedModel ? (
                      <div className="rounded-md border bg-muted/40 p-3 text-sm space-y-3">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-medium">{selectedModel.name}</span>
                          <Badge variant={selectedModel.isFree ? "secondary" : "outline"}>
                            {selectedModel.isFree ? "Бесплатно" : "Платно"}
                          </Badge>
                        </div>
                        <div className="text-xs text-muted-foreground">{selectedModel.id}</div>
                        <Separator />
                        <div className="grid gap-3 sm:grid-cols-3">
                          <div>
                            <div className="text-xs text-muted-foreground">Контекст</div>
                            <div className="font-medium">{formatTokens(selectedModel.contextLength)}</div>
                          </div>
                          <div>
                            <div className="text-xs text-muted-foreground">Макс. вход</div>
                            <div className="font-medium">{formatTokens(selectedModel.maxInputTokens)}</div>
                          </div>
                          <div>
                            <div className="text-xs text-muted-foreground">Макс. выход</div>
                            <div className="font-medium">{formatTokens(selectedModel.maxOutputTokens)}</div>
                          </div>
                        </div>
                        <Separator />
                        <div className="grid gap-3 sm:grid-cols-2">
                          <div>
                            <div className="text-xs text-muted-foreground">Входные модальности</div>
                            <div className="font-medium">{formatModalities(selectedModel.inputModalities)}</div>
                          </div>
                          <div>
                            <div className="text-xs text-muted-foreground">Выходные модальности</div>
                            <div className="font-medium">{formatModalities(selectedModel.outputModalities)}</div>
                          </div>
                        </div>
                        <Separator />
                        <div>
                          <div className="text-xs text-muted-foreground">Стоимость</div>
                          <div className="font-medium">{formatPricing(selectedModel)}</div>
                        </div>
                      </div>
                    ) : (modelValue ? (
                      <div className="rounded-md border border-dashed p-3 text-sm text-muted-foreground">
                        Текущая модель отсутствует в списке OpenRouter. Выберите модель из списка.
                      </div>
                    ) : null)}
                  </div>
                </CardContent>
              </Card>

              {/* Карточка 2: Системный промпт */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <MessageSquare className="h-5 w-5" />
                    Системный промпт
                  </CardTitle>
                  <CardDescription>
                    Инструкции для AI-ассистента
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Textarea
                    id="system-prompt"
                    rows={6}
                    value={config.systemPrompt}
                    onChange={(e) => setConfig({ systemPrompt: e.target.value })}
                    placeholder="Вы — полезный ассистент..."
                  />
                </CardContent>
              </Card>

              {/* Карточка 3: Инструменты */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Wrench className="h-5 w-5" />
                    Инструменты (Tools)
                  </CardTitle>
                  <CardDescription>
                    Позволяет AI использовать функции платформы
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Переключатель инструментов */}
                  <div className="flex items-center justify-between rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <Label htmlFor="enable-tools" className="font-medium">
                        Включить инструменты
                      </Label>
                      <p className="text-sm text-muted-foreground">
                        Проверка подписки, курсы, напоминания, база знаний и др.
                      </p>
                    </div>
                    <Switch
                      id="enable-tools"
                      checked={config.enableTools}
                      onCheckedChange={(checked) => setConfig({ enableTools: checked })}
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Карточка 4: Router Agent */}
              <Card className={cn(!config.enableTools && "opacity-50")}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <Route className="h-5 w-5" />
                        Router Agent
                        <Badge variant="secondary" className="text-xs">Beta</Badge>
                      </CardTitle>
                      <CardDescription>
                        Двухэтапная архитектура для оптимизации ответов
                      </CardDescription>
                    </div>
                    <Switch
                      id="enable-router"
                      checked={config.enableRouter}
                      disabled={!config.enableTools}
                      onCheckedChange={(checked) => setConfig({ enableRouter: checked })}
                    />
                  </div>
                </CardHeader>
                <CardContent className={cn("space-y-4", !config.enableTools && "pointer-events-none")}>
                  {/* Выбор модели роутера */}
                  {config.enableRouter && (
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Модель для Router Agent</Label>
                      <p className="text-xs text-muted-foreground">
                        Рекомендуется быстрая и недорогая модель (gpt-4o-mini, claude-3-haiku)
                      </p>
                      <Popover open={routerModelSelectOpen} onOpenChange={setRouterModelSelectOpen}>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            role="combobox"
                            aria-expanded={routerModelSelectOpen}
                            className="w-full justify-between font-normal"
                            disabled={isLoadingModels}
                          >
                            {isLoadingModels ? (
                              "Загрузка моделей..."
                            ) : (routerModelValue ? (
                              selectedRouterModel?.name || routerModelValue
                            ) : (
                              "Выберите модель роутера..."
                            ))}
                            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
                          <Command>
                            <CommandInput placeholder="Поиск модели..." />
                            <CommandList>
                              <CommandEmpty>Модели не найдены</CommandEmpty>
                              <CommandGroup>
                                {models.map((model) => (
                                  <CommandItem
                                    key={model.id}
                                    value={`${model.name} ${model.id}`}
                                    onSelect={() => {
                                      setConfig({ routerModel: model.id });
                                      setRouterModelSelectOpen(false);
                                    }}
                                  >
                                    <Check
                                      className={cn(
                                        "mr-2 h-4 w-4",
                                        routerModelValue === model.id ? "opacity-100" : "opacity-0"
                                      )}
                                    />
                                    <div className="flex flex-col">
                                      <span>{model.name || model.id}</span>
                                      {model.isFree && (
                                        <span className="text-xs text-muted-foreground">бесплатно</span>
                                      )}
                                    </div>
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                      {selectedRouterModel && (
                        <div className="text-xs text-muted-foreground">
                          Контекст: {formatTokens(selectedRouterModel.contextLength)} |
                          Стоимость: {formatPricing(selectedRouterModel)}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Как это работает */}
                  <Collapsible open={routerInfoOpen} onOpenChange={setRouterInfoOpen}>
                    <CollapsibleTrigger asChild>
                      <Button variant="ghost" size="sm" className="w-full justify-between">
                        <span className="flex items-center gap-2">
                          <Info className="h-4 w-4" />
                          Как это работает?
                        </span>
                        {routerInfoOpen ? (
                          <ChevronUp className="h-4 w-4" />
                        ) : (
                          <ChevronDown className="h-4 w-4" />
                        )}
                      </Button>
                    </CollapsibleTrigger>
                    <CollapsibleContent className="space-y-4 pt-4">
                      <Alert>
                        <Zap className="h-4 w-4" />
                        <AlertTitle>Двухэтапная обработка запросов</AlertTitle>
                        <AlertDescription className="mt-2">
                          Каждый запрос пользователя обрабатывается в два этапа:
                        </AlertDescription>
                      </Alert>

                      <div className="grid gap-4 md:grid-cols-2">
                        {/* Этап 1 */}
                        <div className="rounded-lg border bg-muted/50 p-4 space-y-3">
                          <div className="flex items-center gap-2">
                            <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-bold">
                              1
                            </div>
                            <span className="font-semibold">Router Agent</span>
                            <Badge variant="outline" className="text-xs">
                              {selectedRouterModel?.name || routerModelValue}
                            </Badge>
                          </div>
                          <p className="text-sm text-muted-foreground">
                            Быстрая модель анализирует запрос:
                          </p>
                          <ul className="text-sm space-y-2">
                            <li className="flex items-start gap-2">
                              <Brain className="h-4 w-4 mt-0.5 text-blue-500" />
                              <span><strong>analyze_intent</strong> — определяет намерение</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <Wrench className="h-4 w-4 mt-0.5 text-orange-500" />
                              <span><strong>select_tools</strong> — выбирает инструменты</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <Search className="h-4 w-4 mt-0.5 text-green-500" />
                              <span><strong>search_knowledge</strong> — ищет в базе знаний</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <MessageSquare className="h-4 w-4 mt-0.5 text-purple-500" />
                              <span><strong>get_relevant_history</strong> — находит историю</span>
                            </li>
                          </ul>
                        </div>

                        {/* Этап 2 */}
                        <div className="rounded-lg border bg-muted/50 p-4 space-y-3">
                          <div className="flex items-center gap-2">
                            <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-bold">
                              2
                            </div>
                            <span className="font-semibold">Executor Agent</span>
                            <Badge variant="outline" className="text-xs">основная модель</Badge>
                          </div>
                          <p className="text-sm text-muted-foreground">
                            Основная модель получает обогащённый контекст:
                          </p>
                          <ul className="text-sm space-y-2">
                            <li className="flex items-start gap-2">
                              <Check className="h-4 w-4 mt-0.5 text-green-500" />
                              <span>Только нужные инструменты</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <Check className="h-4 w-4 mt-0.5 text-green-500" />
                              <span>Информация из базы знаний</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <Check className="h-4 w-4 mt-0.5 text-green-500" />
                              <span>Контекст из истории чата</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <Check className="h-4 w-4 mt-0.5 text-green-500" />
                              <span>Понимание намерения</span>
                            </li>
                          </ul>
                        </div>
                      </div>

                      <div className="rounded-lg border border-dashed p-4 space-y-2">
                        <div className="font-medium">Преимущества:</div>
                        <ul className="text-sm text-muted-foreground space-y-1">
                          <li>• <strong>Экономия токенов</strong> — модель не получает все 30+ инструментов</li>
                          <li>• <strong>Быстрее ответы</strong> — меньше итераций tool calls</li>
                          <li>• <strong>Точнее контекст</strong> — предзагруженная информация из БЗ</li>
                          <li>• <strong>Умнее ответы</strong> — учитывается история разговоров</li>
                        </ul>
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                </CardContent>
              </Card>

              {/* Кнопка сохранения */}
            </>
          )}
          </div>
        </TabsContent>

        <TabsContent value="playground" forceMount className="mt-6 data-[state=inactive]:hidden">
          <Card className="max-w-3xl">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bot className="h-5 w-5" />
                Тестовая песочница
              </CardTitle>
              <CardDescription>
                Отправьте сообщение AI с текущими настройками
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Статус ИИ */}
              <div className="rounded-md border p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Статус ИИ</span>
                  <Badge variant={config.isChatEnabled ? "default" : "secondary"}>
                    {config.isChatEnabled ? "Включен" : "Выключен"}
                  </Badge>
                </div>
                {config.model && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Модель</span>
                    <span className="text-sm font-mono">{config.model}</span>
                  </div>
                )}
                {config.enableRouter && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Router Agent</span>
                    <Badge variant="outline" className="text-xs">
                      {selectedRouterModel?.name || routerModelValue}
                    </Badge>
                  </div>
                )}
                {!config.isChatEnabled && (
                  <p className="text-sm text-muted-foreground">
                    Включите ИИ-чат в настройках для тестирования
                  </p>
                )}
                {config.isChatEnabled && !config.model && (
                  <p className="text-sm text-destructive">
                    Выберите модель в настройках для работы ИИ
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="prompt">Сообщение пользователя</Label>
                <Textarea
                  id="prompt"
                  rows={3}
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  placeholder="Введите вопрос..."
                  disabled={!config.isChatEnabled || !config.model}
                />
              </div>

              {chatResponse && (
                <div className="rounded-lg border bg-muted p-4">
                  <div className="flex items-center gap-2 font-semibold mb-2 text-primary">
                    <Bot className="h-4 w-4" /> Ответ ИИ
                  </div>
                  <div className="text-sm whitespace-pre-wrap">
                    {chatResponse}
                  </div>
                </div>
              )}


            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <FormSpacer />

      {activeTab === "settings" && (
        <FormStickyActions
          primaryLabel={isUpdating ? "Сохранение..." : "Сохранить настройки"}
          secondaryLabel="Отмена"
          onPrimary={handleSaveSettings}
          onSecondary={() => {
            setConfigOverrides({});
            refetchSettings();
          }}
          isPending={isUpdating}
        />
      )}

      {activeTab === "playground" && (
        <FormStickyActions
          primaryLabel={isChatting ? "Отправка..." : "Отправить"}
          secondaryLabel="Очистить"
          onPrimary={handleSendChat}
          onSecondary={() => {
            setPrompt("");
            setChatResponse("");
          }}
          isPending={isChatting}
          secondaryVariant="outline"
        />
      )}
    </div>
  );
};
